import React from 'react';
import { X } from 'lucide-react';
import WidgetPreview from './WidgetPreview';
import { Agent } from '../types';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent | null;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ isOpen, onClose, agent }) => {
  if (!isOpen || !agent) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center overflow-hidden p-1">
              <img src="/logo-only-white.png" alt="AskProdigy.ai" className="w-full h-full object-contain" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Widget Preview: {agent.config.agent_name}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This is how your widget appears to your customers
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
          <WidgetPreview
            agentName={agent.config.agent_name}
            companyName={agent.config.company_name}
            logoUrlLight={agent.s3_assets?.logo_light || agent.s3_assets?.logo || ''}
            logoUrlDark={agent.s3_assets?.logo_dark || agent.s3_assets?.logo || ''}
            brandColorLight={agent.config.brand_color}
            brandColorDark={agent.config.brand_color_dark || agent.config.brand_color}
            welcomeMessage={agent.config.welcome_message}
            launcherType={agent.config.launcher_icon_type as 'default' | 'custom' || 'default'}
            launcherIconId={agent.config.launcher_icon_id || 'message'}
            launcherIconUrlLight={agent.s3_assets?.launcher_icon_light || agent.s3_assets?.launcher_icon || ''}
            launcherIconUrlDark={agent.s3_assets?.launcher_icon_dark || agent.s3_assets?.launcher_icon || ''}
            showAiIcon={agent.config.show_ai_icon ?? true}
            aiIconPosition={agent.config.ai_icon_position as 'left' | 'right' ?? 'left'}
            showAiLabel={agent.config.show_ai_label ?? true}
            aiLabelText={agent.config.ai_label_text ?? 'AI'}
            showCustomerIcon={agent.config.show_customer_icon ?? true}
            customerIconPosition={agent.config.customer_icon_position as 'left' | 'right' ?? 'left'}
            showCustomerLabel={agent.config.show_customer_label ?? true}
            customerLabelText={agent.config.customer_label_text ?? 'You'}
            customerFontColor={agent.config.customer_font_color ?? '#ffffff'}
            headerLogoShape={agent.config.header_logo_shape as 'square' | 'rectangle' ?? 'square'}
            showHeaderTitles={agent.config.show_header_text ?? true}
            showAgentName={agent.config.show_agent_name ?? true}
            aiIconUrlLight={agent.s3_assets?.ai_icon_light || agent.s3_assets?.ai_icon || ''}
            aiIconUrlDark={agent.s3_assets?.ai_icon_dark || agent.s3_assets?.ai_icon || ''}
            customerIconUrl={agent.s3_assets?.customer_icon || ''}
            avatarEnabled={agent.config.avatar_enabled ?? false}
            showChatHistory={agent.config.enable_chat_history ?? true}
            voiceName={agent.config.voice_name}
            handoffEnabled={agent.config.human_handoff_enabled ?? true}
            handoffButtonVisible={agent.config.human_handoff_button_visible ?? true}
            handoffHoursVisible={agent.config.human_handoff_hours_visible ?? true}
            handoffSchedule={agent.config.human_handoff_schedule ?? null}
          />
        </div>

        {/* Footer */}
        <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
